import React, { useEffect, useState } from "react";
import { ThumbsUp, Star, Users } from "lucide-react";
import axios from "axios";
import Sidebar from "../StudentDashboard/Sidebar";
import Header from "../StudentDashboard/Headers";
import { getInitials, isValidAvatar, renderStars } from "../../config/uiHelpers";
import { API_URL } from "../../config/api";

// ─────────────────────────────────────────────────────
// Review Card — same design as instructor, + instructor info
// ─────────────────────────────────────────────────────
const ReviewCard = ({
  student_name,
  student_avatar,
  course_title,
  review_text,
  review_title,
  rating,
  instructors = [],
}) => {
  const [imageError, setImageError] = useState(false);
  const hasAvatar = isValidAvatar(student_avatar, imageError);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col hover:shadow-md transition-shadow">
      {/* Student avatar + name + course badge */}
      <div className="flex items-start gap-4 mb-4">
        <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
          {hasAvatar ? (
            <img
              src={student_avatar}
              alt={student_name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full bg-[#4AACC5] flex items-center justify-center text-white font-bold text-lg">
              {getInitials(student_name)}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <h3 className="text-base font-bold text-zinc-800">{student_name}</h3>
          <span className="inline-block self-start px-3 py-1 rounded border border-[#4AACC5] text-[#4AACC5] text-xs font-medium truncate max-w-full">
            {course_title}
          </span>
        </div>
      </div>

      {/* Review text */}
      <p className="text-sm text-neutral-700 leading-relaxed mb-4">{review_text}</p>

      {/* ✅ Instructor section */}
      {instructors.length > 0 && (
        <div className="flex items-center gap-2 mb-4 p-2 bg-gray-50 rounded-lg">
          <Users className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-gray-400">Instructor:</span>
            <div className="flex -space-x-1.5">
              {instructors.slice(0, 3).map((ins, i) => (
                <div key={i} style={{ zIndex: 3 - i }} className="relative">
                  {ins.avatar_url ? (
                    <img
                      src={ins.avatar_url}
                      alt={ins.name}
                      title={ins.name}
                      className="w-5 h-5 rounded-full border border-white object-cover"
                    />
                  ) : (
                    <div
                      title={ins.name}
                      className="w-5 h-5 rounded-full border border-white bg-cyan-400 flex items-center justify-center text-white text-[9px] font-bold"
                    >
                      {ins.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <span className="text-xs text-gray-600 font-medium truncate">
              {instructors[0]?.name}
              {instructors.length > 1 ? ` +${instructors.length - 1} more` : ""}
            </span>
          </div>
        </div>
      )}

      {/* Review title + stars */}
      <div className="flex flex-col gap-2 mt-auto">
        <div className="flex items-center gap-2">
          <ThumbsUp className="w-4 h-4 text-zinc-800" />
          <span className="text-sm font-medium text-black">{review_title}</span>
        </div>
        <div className="flex gap-1 items-center">{renderStars(rating, Star)}</div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────
// Empty State
// ─────────────────────────────────────────────────────
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <Star className="w-8 h-8 text-gray-400" />
    </div>
    <p className="text-gray-500 text-lg font-medium">No reviews available yet</p>
    <p className="text-gray-400 text-sm mt-2">
      Reviews will appear here once students submit them
    </p>
  </div>
);

// ─────────────────────────────────────────────────────
// MAIN PAGE
// Route: <Route path="/admin_review" element={<PrivateRoute><AdminReviewPage /></PrivateRoute>} />
// ─────────────────────────────────────────────────────
export default function AdminReviewPage() {
  const [userData, setUserData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const reviewsPerPage = 9;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) throw new Error("No access token found.");

        const headers = { Authorization: `Bearer ${token}` };

        // Fetch user + reviews in parallel
        const [currentUserRes, reviewsRes] = await Promise.all([
          axios.get(`${API_URL}/current_user`, { headers }),
          axios.get(`${API_URL}/courses/admin_reviews`, { headers }),
        ]);

        const userId = currentUserRes.data?.user_id;
        if (userId) {
          const userDetailRes = await axios.get(`${API_URL}/user/${userId}`, { headers });
          setUserData(userDetailRes.data?.data || userDetailRes.data || null);
        }

        // Response: { success, message, data: { total_reviews, reviews: [...] } }
        const reviewList = reviewsRes.data?.data?.reviews || [];
        setReviews(reviewList);
      } catch (err) {
        console.error("Error fetching admin reviews:", err);
        setError(err.response?.data?.detail || err.message || "Failed to load reviews.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Pagination
  const totalPages = Math.ceil(reviews.length / reviewsPerPage) || 1;
  const currentReviews = reviews.slice(
    (currentPage - 1) * reviewsPerPage,
    currentPage * reviewsPerPage
  );

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0">
        <Header userData={userData} />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-56 bg-white border-r border-neutral-200 flex-shrink-0">
          <Sidebar />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-7xl mx-auto">
            {/* Page header */}
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-2xl font-bold text-zinc-800">
                Review and Rating
                {!loading && reviews.length > 0 && (
                  <span className="ml-2 text-sm text-gray-400 font-normal">
                    ({reviews.length} total)
                  </span>
                )}
              </h1>

              {/* Pagination controls — top right */}
              {!loading && reviews.length > reviewsPerPage && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 bg-white rounded-lg border border-stone-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm text-gray-500">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 bg-white rounded-lg border border-stone-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* Loading */}
            {loading && (
              <div className="flex justify-center items-center py-20">
                <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Reviews grid */}
            {!loading && !error && (
              currentReviews.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentReviews.map((review, index) => (
                    <ReviewCard key={index} {...review} />
                  ))}
                </div>
              ) : (
                <EmptyState />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}